import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { catchError, of, timeout } from 'rxjs';
import { Helpers } from 'src/app/models/helpers';
import { AuthService } from 'src/app/services/auth.service';
import Swal from 'sweetalert2';
import { APP_VERSION } from 'src/environments/version';
import { ManufacturerPopoversService } from 'src/app/services/shared/manufacturer-popovers.service';

/**
 * Login page — entry point della webapp per admin, operator e screen.
 *
 * ─── Restyle v2.14.2 ─────────────────────────────────────────────
 * Il template è stato migrato da Kendo (`kendo-formfield`,
 * `kendo-textbox`, `kendo-label`) a input HTML nativi + reactive
 * forms. Motivazione:
 *   1. Coerenza col resto della webapp (gestione-account,
 *      settings, chat) che usa già native input + design token.
 *   2. Un dependency in meno da `@progress/kendo-angular-inputs`
 *      (già ridotto pesantemente in v2.14.1 T2b).
 *   3. Il toggle mostra/nascondi password ora è pilotato da
 *      un semplice signal `passwordVisible` senza `@ViewChild`
 *      sul TextBoxComponent Kendo.
 *
 * `NotificationService` (Kendo) resta perché usato in tutta l'app
 * per lo stack notify — sarà smontato in una fase futura di
 * migration a un servizio Angular-native.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  invalidLogin = false;
  wrongLogin = '';
  ipAddress = '';
  currentYear = new Date().getFullYear();
  /** Toggle "mostra/nascondi password". Bindato al `[type]` dell'input. */
  passwordVisible = false;

  // Shown in the login success Swal. Kept as plain semver (no commit
  // hash) to keep the greeting clean — sidebar shows the full detail.
  public desktopVersion: string = APP_VERSION;
  public mServ = inject(ManufacturerPopoversService);

  constructor(
    private authServ: AuthService,
    private router: Router,
    private http: HttpClient,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getIPAddress();
  }

  /** Switcha il tipo dell'input password tra "password" e "text". */
  public toggleVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  /**
   * Fetch dell'IP pubblico dell'utente da `api.ipify.org` per popolare
   * `this.ipAddress`, che viene passato al BE `POST /login/` come
   * `ip_address`. Il BE lo usa per session tracking e (potenzialmente)
   * IP-based validation — se il payload arriva con `ip_address: ""`,
   * la response è `msg_code: 8, "No data returned by the database"`.
   *
   * 🚨 FIX v2.14.8 (E2E CI investigation, 2026-07-20): pre-fix la
   * chiamata era senza timeout e senza catchError. Se `api.ipify.org`
   * era lento o irraggiungibile (rete VPN aziendale, DNS problem,
   * firewall, service degradation di ipify), `this.ipAddress` restava
   * `""` e ogni tentativo di login falliva silenziosamente con
   * `msg_code: 8` → notification transiente 600ms → utente non capisce
   * cosa succede.
   *
   * Fix: timeout(3000) per non bloccare l'UX se ipify non risponde,
   * catchError con fallback `0.0.0.0` per garantire un IP-shaped
   * string al BE. Log warn per audit trail (utile a debug futuri).
   */
  getIPAddress() {
    this.http
      .get('https://api.ipify.org/?format=json')
      .pipe(
        timeout(3000),
        catchError((err) => {
          console.warn(
            '[Login] api.ipify.org non raggiungibile, uso IP fallback 0.0.0.0',
            err,
          );
          return of({ ip: '0.0.0.0' });
        }),
      )
      .subscribe((res: any) => {
        this.ipAddress = res.ip;
        this.cdr.detectChanges();
      });
  }

  public loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-z]{2,4}$'),
    ]),
    password: new FormControl('', Validators.required),
    keepLogged: new FormControl(),
  });

  public login(formValues: any): void {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.invalid) return;

    this.authServ.clearUser();
    this.authServ
      .login(formValues.email, formValues.password, this.ipAddress)
      .subscribe((res: any) => {
        const msgCode = res?.error_message?.msg_code;
        const msgText = res?.error_message?.msg_text;

        switch (msgCode) {
          case 0:
            this.router.navigateByUrl(
              this.resolveTargetUrl(res?.data?.ac_user_type_cod),
            );
            if (
              this.authServ.permissionUsed.value !==
              Helpers.PermisionManufacturer
            ) {
              Swal.fire({
                icon: 'success',
                title: `Diakonix ${this.desktopVersion}`,
                text: 'Buon lavoro! ;)',
                timer: 3000,
                timerProgressBar: true,
                showCancelButton: false,
                showConfirmButton: false,
              });
            }
            break;

          // 🚨 FIX v2.14.8 UX (2026-07-20): messaggi errore login mirati.
          // Pre-fix: tutti gli errori usavano Kendo `showNotification`
          // con `hideAfter: 600ms` — visualizzati per mezzo secondo poi
          // sparivano. Utenti (e maintainer in debug) non facevano in
          // tempo a leggere. Nuova strategia:
          //   • messaggio SPECIFICO per ogni msg_code noto,
          //   • messaggio con `msg_code` esposto nel default (per
          //     ticket support / debug),
          //   • Swal persistente (button OK) invece che notification
          //     transiente — l'utente deve prendere azione, non deve
          //     doverlo intuire in 600ms.
          case 9:
            this.showLoginError('Password errata', 'La password inserita non è corretta.');
            return;
          case 12:
            this.showLoginError('Account non attivo', 'Il tuo account è stato disattivato. Contatta l\'amministratore.');
            return;
          case 13:
            this.showLoginError('Account scaduto', 'Il tuo account è scaduto. Contatta l\'amministratore per rinnovarlo.');
            return;
          case 14:
            this.showLoginError('Utente inesistente', 'Nessun account trovato con questa email. Verifica l\'indirizzo o richiedi la registrazione.');
            return;
          case 8:
            // "No data was returned by the database" — combinazione
            // email/password/IP non matcha nessun record. Nel dubbio
            // proponiamo il caso più comune (credenziali) senza
            // escludere gli altri.
            this.showLoginError(
              'Credenziali non valide',
              'La combinazione email/password non è corretta oppure l\'account presenta un problema di configurazione. Riprova o contatta l\'amministratore.',
            );
            return;
          default:
            // Errore non previsto — mostriamo msg_code e msg_text del
            // BE per rendere il debug possibile (screenshot utente →
            // ticket support tracciabile).
            this.showLoginError(
              'Errore imprevisto',
              `Impossibile completare il login. Riprova più tardi o contatta il supporto${msgCode !== undefined ? ` (codice: ${msgCode})` : ''}${msgText ? `.\nDettaglio tecnico: ${msgText}` : '.'}`,
            );
            return;
        }
      });
  }

  /**
   * Mostra un errore di login persistente via Swal.
   *
   * Sostituisce la Kendo `showNotification` con `hideAfter: 600ms` che
   * spariva prima che l'utente potesse leggerla. Swal resta finché
   * l'utente non conferma → nessun errore silenzioso.
   */
  private showLoginError(title: string, text: string): void {
    Swal.fire({
      icon: 'error',
      title,
      text,
      confirmButtonText: 'Ho capito',
      confirmButtonColor: 'var(--brand-primary, #017efa)',
    });
  }

  public clearForm(): void {
    this.loginForm.reset();
  }

  private resolveTargetUrl(userType?: Helpers.Permission): string {
    if (userType === Helpers.PermisionScreen) {
      return '/warehouseorders';
    }

    const currentStateUrl = this.authServ.stateUrl || 'dashboard';
    const normalizedStateUrl = currentStateUrl.startsWith('/')
      ? currentStateUrl
      : `/${currentStateUrl}`;

    if (
      userType === Helpers.PermisionManufacturer &&
      normalizedStateUrl === '/dashboard'
    ) {
      return '/dashboard';
    }

    return normalizedStateUrl;
  }

  public showNotification(
    messageText: string,
    styleType: 'none' | 'success' | 'warning' | 'error' | 'info',
  ): void {
    this.notificationService.show({
      content: messageText,
      hideAfter: 600,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'fade', duration: 2000 },
      type: { style: styleType, icon: true },
    });
  }
}
